"use client";

import * as React from "react";
import { Text, SectionCard, CardTitle, CardDescription, Badge, Button, Skeleton, Input, Section } from "@/src/components";
import {
  useVirtualList,
  useDebouncedValue,
  useIntersectionObserver,
} from "@/lib/performance";

export default function DocsPerformance() {
  // Virtual list demo
  const { containerRef, totalHeight, virtualItems } = useVirtualList({
    itemCount: 10000,
    itemHeight: 40,
    overscan: 5,
  });

  // Debounce demo
  const [search, setSearch] = React.useState("");
  const debouncedSearch = useDebouncedValue(search, 300);

  // Intersection observer demo
  const { ref: lazyRef, isIntersecting } = useIntersectionObserver({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Performance Guidelines
            </Text>
            <Badge variant="secondary">Phase 9</Badge>
          </div>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            Optimize your application with these performance utilities and best practices
            for bundle size, rendering, and user experience.
          </Text>
        </div>
      </Section>

      {/* Bundle Size Targets */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Bundle Size Targets</CardTitle>
            <CardDescription>Maximum sizes per component type</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 pr-4 font-medium">Component Type</th>
                <th className="text-left py-3 pr-4 font-medium">Max Size</th>
                <th className="text-left py-3 font-medium">Examples</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              <tr>
                <td className="py-3 pr-4">
                  <Badge variant="outline">Simple</Badge>
                </td>
                <td className="py-3 pr-4 font-mono text-primary">&lt; 2KB</td>
                <td className="py-3 text-muted-foreground">Button, Badge, Text</td>
              </tr>
              <tr>
                <td className="py-3 pr-4">
                  <Badge variant="outline">Medium</Badge>
                </td>
                <td className="py-3 pr-4 font-mono text-primary">&lt; 5KB</td>
                <td className="py-3 text-muted-foreground">Input, Select, Switch</td>
              </tr>
              <tr>
                <td className="py-3 pr-4">
                  <Badge variant="outline">Complex</Badge>
                </td>
                <td className="py-3 pr-4 font-mono text-primary">&lt; 15KB</td>
                <td className="py-3 text-muted-foreground">DataTable, DateRangePicker</td>
              </tr>
              <tr>
                <td className="py-3 pr-4">
                  <Badge variant="outline">Blocks</Badge>
                </td>
                <td className="py-3 pr-4 font-mono text-primary">&lt; 25KB</td>
                <td className="py-3 text-muted-foreground">Sidebar, FilterBuilder</td>
              </tr>
            </tbody>
          </table>
        </div>
      </SectionCard>
      </Section>

      {/* Optimization Techniques */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Optimization Techniques</CardTitle>
            <CardDescription>Core principles for performant components</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-4"
      >
        <ul className="space-y-3 text-sm">
          <li className="flex gap-3">
            <Badge className="shrink-0">1</Badge>
            <div>
              <strong>Tree-shaking friendly exports</strong>
              <p className="text-muted-foreground mt-1">Use named exports, avoid barrel files for large modules</p>
            </div>
          </li>
          <li className="flex gap-3">
            <Badge className="shrink-0">2</Badge>
            <div>
              <strong>Lazy load heavy components</strong>
              <p className="text-muted-foreground mt-1">Use <code className="text-xs bg-muted px-1 py-0.5 rounded">lazyWithFallback()</code> for DataTable, DateRangePicker</p>
            </div>
          </li>
          <li className="flex gap-3">
            <Badge className="shrink-0">3</Badge>
            <div>
              <strong>Memoize expensive computations</strong>
              <p className="text-muted-foreground mt-1">Use <code className="text-xs bg-muted px-1 py-0.5 rounded">useMemo()</code>, <code className="text-xs bg-muted px-1 py-0.5 rounded">useCallback()</code>, and <code className="text-xs bg-muted px-1 py-0.5 rounded">React.memo()</code></p>
            </div>
          </li>
          <li className="flex gap-3">
            <Badge className="shrink-0">4</Badge>
            <div>
              <strong>Virtualize long lists</strong>
              <p className="text-muted-foreground mt-1">Use <code className="text-xs bg-muted px-1 py-0.5 rounded">useVirtualList()</code> for lists with 100+ items</p>
            </div>
          </li>
          <li className="flex gap-3">
            <Badge className="shrink-0">5</Badge>
            <div>
              <strong>Use CSS for animations</strong>
              <p className="text-muted-foreground mt-1">Prefer CSS transitions over JavaScript animations</p>
            </div>
          </li>
        </ul>
      </SectionCard>
      </Section>

      {/* Virtual List Demo */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Virtual List</CardTitle>
            <CardDescription>Render 10,000 items efficiently</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div
          ref={containerRef}
          className="h-64 overflow-auto border rounded-lg bg-muted/30"
        >
          <div style={{ height: totalHeight, position: "relative" }}>
            {virtualItems.map(({ index, style }: { index: number; style: React.CSSProperties }) => (
              <div
                key={index}
                style={style}
                className="flex items-center px-4 border-b bg-background hover:bg-accent/50"
              >
                <Badge variant="outline" className="mr-3">{index + 1}</Badge>
                <Text className="text-sm">Virtual row item {index + 1}</Text>
              </div>
            ))}
          </div>
        </div>
        <Text className="text-xs text-muted-foreground mt-2">
          Scroll to see virtualization in action. Only visible items are rendered.
        </Text>

        <div className="bg-muted/50 rounded-lg p-4 font-mono text-xs overflow-x-auto mt-4">
          <code>{`const { containerRef, totalHeight, virtualItems } = useVirtualList({
  itemCount: 10000,
  itemHeight: 40,
  overscan: 5,
});`}</code>
        </div>
      </SectionCard>
      </Section>

      {/* Debounce Demo */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Debounced Value</CardTitle>
            <CardDescription>Delay updates for search inputs</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-4"
      >
        <div className="space-y-2">
          <Input
            placeholder="Type to search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="flex gap-4 text-sm">
            <div>Immediate: <code className="bg-muted px-1 rounded">{search || "(empty)"}</code></div>
            <div>Debounced (300ms): <code className="bg-muted px-1 rounded">{debouncedSearch || "(empty)"}</code></div>
          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-4 font-mono text-xs">
          <code>{`const debouncedSearch = useDebouncedValue(search, 300);`}</code>
        </div>
      </SectionCard>
      </Section>

      {/* Lazy Loading Demo */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Lazy Loading</CardTitle>
            <CardDescription>Load content when visible</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-4"
      >
        <div
          ref={lazyRef}
          className="p-6 border rounded-lg min-h-[120px] flex items-center justify-center"
        >
          {isIntersecting ? (
            <div className="text-center space-y-2">
              <Badge variant="default">Loaded!</Badge>
              <Text className="text-sm text-muted-foreground">
                This content was loaded when it entered the viewport
              </Text>
            </div>
          ) : (
            <Skeleton className="h-16 w-48" />
          )}
        </div>

        <div className="bg-muted/50 rounded-lg p-4 font-mono text-xs overflow-x-auto">
          <code>{`const { ref, isIntersecting } = useIntersectionObserver({
  threshold: 0.1,
  triggerOnce: true,
});

return <div ref={ref}>{isIntersecting && <HeavyComponent />}</div>;`}</code>
        </div>
      </SectionCard>
      </Section>

      {/* Performance Checklist */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Component Performance Checklist</CardTitle>
            <CardDescription>Every component should follow these rules</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 rounded-lg border">
            <div className="size-5 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs">✓</div>
            <Text className="text-sm">Use <code className="bg-muted px-1 rounded">React.memo()</code> for pure functional components</Text>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg border">
            <div className="size-5 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs">✓</div>
            <Text className="text-sm">Use <code className="bg-muted px-1 rounded">useCallback()</code> for event handlers passed to children</Text>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg border">
            <div className="size-5 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs">✓</div>
            <Text className="text-sm">Use <code className="bg-muted px-1 rounded">useMemo()</code> for expensive computations</Text>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg border">
            <div className="size-5 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs">✓</div>
            <Text className="text-sm">Avoid inline object/array creation in JSX props</Text>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg border">
            <div className="size-5 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs">✓</div>
            <Text className="text-sm">Use CSS transitions instead of JavaScript animations</Text>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg border">
            <div className="size-5 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs">✓</div>
            <Text className="text-sm">Split large components into smaller, focused pieces</Text>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Available Utilities */}
      <Section>
        <SectionCard
        header={<CardTitle>Performance Utilities</CardTitle>}
        contentClassName="p-6"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4 font-medium">Utility</th>
                <th className="text-left py-2 font-medium">Purpose</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">lazyWithFallback()</td>
                <td className="py-2 text-muted-foreground">Lazy load with Suspense fallback</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">preloadComponent()</td>
                <td className="py-2 text-muted-foreground">Preload lazy component on hover</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">useStableCallback()</td>
                <td className="py-2 text-muted-foreground">Stable callback reference</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">useMemoCompare()</td>
                <td className="py-2 text-muted-foreground">Custom comparison memoization</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">useDebouncedValue()</td>
                <td className="py-2 text-muted-foreground">Debounce value changes</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">useThrottledCallback()</td>
                <td className="py-2 text-muted-foreground">Throttle callback execution</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">useIntersectionObserver()</td>
                <td className="py-2 text-muted-foreground">Viewport visibility detection</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">useVirtualList()</td>
                <td className="py-2 text-muted-foreground">Virtualize long lists</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">useRenderTime()</td>
                <td className="py-2 text-muted-foreground">Measure component render time (dev)</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">useWhyDidYouRender()</td>
                <td className="py-2 text-muted-foreground">Debug re-render causes (dev)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </SectionCard>
      </Section>

      {/* Import */}
      <Section>
        <SectionCard
        header={<CardTitle>Import</CardTitle>}
        contentClassName="p-6"
      >
        <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto">
          <code>{`import {
  lazyWithFallback,
  preloadComponent,
  useStableCallback,
  useDebouncedValue,
  useThrottledCallback,
  useIntersectionObserver,
  useVirtualList,
} from "@/lib/performance";`}</code>
        </div>
      </SectionCard>
      </Section>
    </>
  );
}
